import React, { useState, useEffect } from 'react';
import { sameIdentity } from '../../services/identity';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { AdminService } from '../../services/admin';
import { isDemoMode, DEMO_USERS } from '../../services/mockData';
import { notifyExpenseFinanceApproved, notifyExpenseFinanceRejected, notifyExpenseReimbursed } from '../../services/inAppNotification';
import { emailExpenseFinanceApproved, emailExpenseFinanceRejected, emailExpenseReimbursed } from '../../services/emailNotification';
import { useNotifications } from '../../contexts/NotificationContext';
import { Expense, ExpenseStatus } from '../../types/expense';
import { User, UserRole } from '../../types/user';
import ExpenseDetail from '../expenses/ExpenseDetail';
import InlineLoader from '../common/InlineLoader';
import './SuperAdminDashboard.css';

interface DashboardMetrics {
  totalUsers: number;
  totalExpenses: number;
  pendingApprovals: number;
  totalAmount: number;
}

/**
 * Super Admin Dashboard Component
 * Requirements: Super Admin role with user management and expense approval capabilities
 */
const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const navigate = useNavigate();
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<Expense[]>([]);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'expenses' | 'approvals'>('overview');
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalUsers: 0,
    totalExpenses: 0,
    pendingApprovals: 0,
    totalAmount: 0
  });
  
  // Filter states for All Expenses tab
  const [showExpenseFilters, setShowExpenseFilters] = useState(false);
  const [filteredExpenses, setFilteredExpenses] = useState<Expense[]>([]);
  const [expenseFilters, setExpenseFilters] = useState({
    manager: '', // email of selected manager
    employee: '', // email of selected employee
    status: '' as ExpenseStatus | '',
    datePreset: 'all' as 'all' | 'last-week' | 'last-month' | 'custom',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  // Apply filters when allExpenses or filters change
  useEffect(() => {
    applyExpenseFilters();
  }, [allExpenses, expenseFilters, allUsers]);

  // Helper to get user info by submitterId
  const getUserInfo = (submitterId: string): { name: string; email: string; managerName?: string; managerEmail?: string } => {
    // sameIdentity, not a lowercased ===: a submitter's expense may be stored against either
    // of the two domain addresses they own, and an exact match resolved only one of them —
    // leaving the other showing no submitter name at all.
    const foundUser = allUsers.find(u =>
      u.id === submitterId ||
      sameIdentity(u.email, submitterId)
    );
    
    if (foundUser) {
      let managerName: string | undefined;
      let managerEmail = foundUser.managerEmail;
      if (managerEmail) {
        const manager = allUsers.find(u => sameIdentity(u.email, managerEmail));
        managerName = manager?.name;
      }
      
      return {
        name: foundUser.name,
        email: foundUser.email,
        managerName,
        managerEmail
      };
    }
    
    if (submitterId.includes('@')) {
      const namePart = submitterId.split('@')[0];
      return {
        name: namePart.split(/[._-]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        email: submitterId
      };
    }
    
    return { name: 'Unknown', email: submitterId };
  };

  // Apply filters to expenses
  const applyExpenseFilters = () => {
    let filtered = [...allExpenses];
    
    // Filter by manager (show expenses from employees who report to this manager)
    if (expenseFilters.manager) {
      /*
       * Every comparison here is sameIdentity rather than lowercased string equality. Each
       * person owns two accounts on two domains, so matching a report to its manager, and then
       * an expense to that report, both dropped whichever half was filed under the other
       * address — a manager filter that quietly returned a subset of their team's expenses.
       */
      const teamEmails = allUsers
        .filter(u => sameIdentity(u.managerEmail, expenseFilters.manager))
        .map(u => u.email);

      filtered = filtered.filter(expense => {
        const submitterEmail = getUserInfo(expense.submitterId).email;
        return teamEmails.some(e => sameIdentity(e, submitterEmail));
      });
    }
    
    // Filter by specific employee
    if (expenseFilters.employee) {
      filtered = filtered.filter(expense =>
        sameIdentity(getUserInfo(expense.submitterId).email, expenseFilters.employee)
      );
    }
    
    // Filter by status
    if (expenseFilters.status) {
      filtered = filtered.filter(expense => expense.status === expenseFilters.status);
    }
    
    // Filter by date
    if (expenseFilters.dateFrom) {
      filtered = filtered.filter(expense => expense.date >= expenseFilters.dateFrom);
    }
    if (expenseFilters.dateTo) {
      filtered = filtered.filter(expense => expense.date <= expenseFilters.dateTo);
    }
    
    setFilteredExpenses(filtered);
  };

  // Handle date preset changes for expense filters
  const handleExpenseDatePresetChange = (preset: 'all' | 'last-week' | 'last-month' | 'custom') => {
    const now = new Date();
    let dateFrom = '';
    let dateTo = '';
    
    if (preset === 'last-week') {
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      dateFrom = lastWeek.toISOString().split('T')[0];
      dateTo = now.toISOString().split('T')[0];
    } else if (preset === 'last-month') {
      const lastMonth = new Date(now);
      lastMonth.setMonth(now.getMonth() - 1);
      dateFrom = lastMonth.toISOString().split('T')[0];
      dateTo = now.toISOString().split('T')[0];
    }
    
    setExpenseFilters(prev => ({
      ...prev,
      datePreset: preset,
      dateFrom: preset === 'custom' ? prev.dateFrom : dateFrom,
      dateTo: preset === 'custom' ? prev.dateTo : dateTo
    }));
  };

  // Clear expense filters
  const clearExpenseFilters = () => {
    setExpenseFilters({
      manager: '',
      employee: '',
      status: '',
      datePreset: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Get list of managers for filter dropdown
  const getManagers = (): User[] => {
    return allUsers.filter(u => 
      u.role === UserRole.MANAGER || 
      u.role === UserRole.FINANCE_ADMIN || 
      u.role === UserRole.SUPER_ADMIN
    );
  };

  // Get list of employees for filter dropdown
  const getEmployees = (): User[] => {
    // If a manager is selected, only show their direct reports
    if (expenseFilters.manager) {
      return allUsers.filter(u => sameIdentity(u.managerEmail, expenseFilters.manager));
    }
    return allUsers;
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load expenses
      const expensesResult = await ExpenseServiceWrapper.getAllExpenses(user?.role);
      
      // Load users - prioritize demo mode, then Cognito, then GraphQL
      let users: User[] = [];
      
      console.log('[SuperAdminDashboard] Loading users...');
      console.log('[SuperAdminDashboard] Environment check:');
      console.log('  REACT_APP_DEMO_MODE:', process.env.REACT_APP_DEMO_MODE);
      console.log('  REACT_APP_ADMIN_API_ENDPOINT:', process.env.REACT_APP_ADMIN_API_ENDPOINT);
      console.log('  All process.env keys starting with REACT_APP:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));
      console.log('  Cognito configured:', CognitoAdminService.isConfigured());
      console.log('  isDemoMode():', isDemoMode());
      
      // Force check the environment variable directly
      const adminEndpoint = process.env.REACT_APP_ADMIN_API_ENDPOINT;
      const isCognitoConfigured = !!(adminEndpoint && adminEndpoint.length > 0);
      
      console.log('[SuperAdminDashboard] Direct environment check:');
      console.log('  adminEndpoint:', adminEndpoint);
      console.log('  isCognitoConfigured:', isCognitoConfigured);
      
      if (isDemoMode()) {
        // Use demo mode first if enabled
        console.log('[SuperAdminDashboard] Using demo users (demo mode enabled)');
        const storedUsers = localStorage.getItem('demo_users');
        users = storedUsers ? JSON.parse(storedUsers) : DEMO_USERS;
      } else if (isCognitoConfigured) {
        // Use Cognito Admin API when configured
        console.log('[SuperAdminDashboard] Loading users from Cognito Admin API');
        
        // Check if we have authentication context for Cognito API calls
        if (!user) {
          console.log('[SuperAdminDashboard] Waiting for authentication context...');
          throw new Error('Waiting for authentication...');
        }
        
        try {
          const response = await CognitoAdminService.listUsers();
          console.log('[SuperAdminDashboard] Raw Cognito response:', response);
          
          if (response.users) {
            console.log('[SuperAdminDashboard] Processing', response.users.length, 'users from Cognito');
            
            // Convert Cognito users to User interface format
            users = response.users.map((cognitoUser: any, index: number) => {
              console.log(`[SuperAdminDashboard] Processing user ${index + 1}:`, cognitoUser);
              console.log(`[SuperAdminDashboard] Raw Cognito user structure:`, JSON.stringify(cognitoUser, null, 2));
              
              // Handle both direct properties and Attributes array format
              const getAttributeValue = (attrName: string) => {
                // For this Cognito response format, attributes are direct properties
                // Try the attribute name without 'custom:' prefix first
                const directAttrName = attrName.replace('custom:', '');
                if (cognitoUser[directAttrName]) {
                  console.log(`[SuperAdminDashboard] Found ${attrName} as direct property '${directAttrName}':`, cognitoUser[directAttrName]);
                  return cognitoUser[directAttrName];
                }
                
                // Try direct property with full name
                if (cognitoUser[attrName]) {
                  console.log(`[SuperAdminDashboard] Found ${attrName} as direct property:`, cognitoUser[attrName]);
                  return cognitoUser[attrName];
                }
                
                // Try Attributes array (fallback for other Cognito formats)
                if (cognitoUser.Attributes && Array.isArray(cognitoUser.Attributes)) {
                  const attr = cognitoUser.Attributes.find((attr: any) => attr.Name === attrName);
                  if (attr) {
                    console.log(`[SuperAdminDashboard] Found ${attrName} in Attributes array:`, attr.Value);
                    return attr.Value;
                  }
                }
                console.log(`[SuperAdminDashboard] ${attrName} not found in user data`);
                return undefined;
              };
              
              const email = getAttributeValue('email') || cognitoUser.Username || '';
              const name = getAttributeValue('name') || getAttributeValue('given_name') || 'Unknown';
              const employeeId = getAttributeValue('custom:employee_id') || getAttributeValue('employeeId') || '';
              const department = getAttributeValue('custom:department') || getAttributeValue('department') || '';
              const managerEmail = getAttributeValue('custom:manager_email') || getAttributeValue('managerEmail') || undefined;
              const rawRole = getAttributeValue('custom:role') || getAttributeValue('role');
              
              // Debug: Show all available attributes
              console.log(`[SuperAdminDashboard] All direct properties for ${email}:`, Object.keys(cognitoUser).map(key => `${key}: ${cognitoUser[key]}`).join(', '));
              
              // Use centralized role mapping
              const role = CognitoAdminService.mapCognitoRole(rawRole);
              
              console.log(`[SuperAdminDashboard] Final extracted data for ${email}:`, {
                name, role, department, managerEmail, employeeId, rawRole
              });
              
              return {
                id: cognitoUser.Username || email,
                email,
                name,
                employeeId,
                department,
                managerEmail,
                role, // Use the correctly mapped role
                createdAt: cognitoUser.UserCreateDate || cognitoUser.createdAt || new Date().toISOString(),
                updatedAt: cognitoUser.UserLastModifiedDate || cognitoUser.updatedAt || new Date().toISOString()
              };
            });
            
            console.log('[SuperAdminDashboard] Converted Cognito users:', users);
            
            // Deduplicate users by email (merge Azure AD users with manual users)
            const usersByEmail = new Map<string, User>();
            users.forEach(u => {
              const existingUser = usersByEmail.get(u.email.toLowerCase());
              if (existingUser) {
                // If existing user is Azure AD (starts with azuread_), prefer the manual user
                if (existingUser.id.startsWith('azuread_') && !u.id.startsWith('azuread_')) {
                  console.log('[SuperAdminDashboard] Replacing Azure user with manual user:', u.email);
                  usersByEmail.set(u.email.toLowerCase(), u);
                } else if (!existingUser.id.startsWith('azuread_') && u.id.startsWith('azuread_')) {
                  // Keep existing manual user, merge any missing data from Azure user
                  console.log('[SuperAdminDashboard] Keeping manual user, skipping Azure duplicate:', u.email);
                }
                // If both are same type, keep the first one
              } else {
                usersByEmail.set(u.email.toLowerCase(), u);
              }
            });
            users = Array.from(usersByEmail.values());
            console.log('[SuperAdminDashboard] Deduplicated users:', users.length);
          } else {
            console.log('[SuperAdminDashboard] No users returned from Cognito');
            users = [];
          }
        } catch (cognitoError) {
          console.error('[SuperAdminDashboard] Failed to load users from Cognito:', cognitoError);
          throw new Error(`Failed to load users from Cognito: ${cognitoError instanceof Error ? cognitoError.message : 'Unknown error'}. Please check your Cognito Admin API configuration.`);
        }
      } else {
        // Fallback to GraphQL only if neither demo mode nor Cognito is configured
        console.log('[SuperAdminDashboard] Using GraphQL fallback');
        
        // Check if we have authentication context for GraphQL API calls
        if (!user) {
          console.log('[SuperAdminDashboard] Waiting for authentication context...');
          throw new Error('Waiting for authentication...');
        }
        
        try {
          users = await AdminService.getAllUsers();
          console.log('[SuperAdminDashboard] GraphQL users:', users);
        } catch (graphqlError) {
          console.error('[SuperAdminDashboard] GraphQL fallback failed:', graphqlError);
          throw new Error(`No user data source available. Cognito Admin API is not configured and GraphQL fallback failed: ${graphqlError instanceof Error ? graphqlError.message : 'Unknown error'}`);
        }
      }
      
      // Filter pending approvals
      const allPending = (expensesResult.items || []).filter(
        e => e.status === ExpenseStatus.MANAGER_APPROVED || e.status === ExpenseStatus.FINANCE_APPROVED
      );
      
      setAllExpenses(expensesResult.items || []);
      setAllUsers(users);
      setPendingApprovals(allPending);
      
      // Calculate metrics
      setMetrics({
        totalUsers: users.length,
        totalExpenses: expensesResult.items?.length || 0,
        pendingApprovals: allPending.length,
        totalAmount: (expensesResult.items || []).reduce((sum, e) => sum + Number(e.amount || 0), 0)
      });

      console.log('[SuperAdminDashboard][Aggregation]', {
        userRole: user?.role,
        recordsScanned: expensesResult.total ?? expensesResult.items?.length ?? 0,
        recordsReturned: expensesResult.items?.length ?? 0,
        totalExpenses: expensesResult.items?.length ?? 0,
        totalAmount: (expensesResult.items || []).reduce((sum, e) => sum + Number(e.amount || 0), 0),
        pendingApprovals: allPending.length,
        appliedFilters: {},
      });
      
    } catch (err) {
      console.error('[Super Admin Dashboard] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalApproval = async (expense: Expense, comments?: string) => {
    try {
      if (user) {
        await ExpenseServiceWrapper.approveExpense(expense.id, user.id, comments);
        notifyExpenseFinanceApproved(expense.submitterId, user.name, expense.title, expense.amount, expense.id);
        emailExpenseFinanceApproved(expense.submitterId, user.name, expense.title, expense.amount, expense.id);
      }
      await loadDashboardData();
      refresh();
    } catch (error) {
      console.error('Failed to approve expense:', error);
      alert('Failed to approve expense. Please try again.');
    }
  };

  const handleMarkReimbursed = async (expense: Expense) => {
    try {
      await ExpenseServiceWrapper.markReimbursed(expense.id);
      notifyExpenseReimbursed(expense.submitterId, expense.title, expense.amount, expense.id);
      emailExpenseReimbursed(expense.submitterId, expense.title, expense.amount, expense.id);
      await loadDashboardData();
      refresh();
    } catch (error) {
      console.error('Failed to mark expense as reimbursed:', error);
      alert('Failed to mark expense as reimbursed. Please try again.');
    }
  };

  const handleRejectExpense = async (expense: Expense, comments: string) => {
    try {
      if (user) {
        await ExpenseServiceWrapper.rejectExpense(expense.id, user.id, comments);
        notifyExpenseFinanceRejected(expense.submitterId, user.name, expense.title, expense.id, comments);
        emailExpenseFinanceRejected(expense.submitterId, user.name, expense.title, expense.id, comments);
      }
      await loadDashboardData();
      refresh();
    } catch (error) {
      console.error('Failed to reject expense:', error);
      alert('Failed to reject expense. Please try again.');
    }
  };

  const handleDeleteExpense = async (expense: Expense) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${expense.title}" (${formatAmount(expense.amount)})?\n\nThis action cannot be undone.`)) {
      return;
    }
    try {
      await ExpenseServiceWrapper.deleteExpense(expense.id);
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alert('Failed to delete expense. Please try again.');
    }
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status: ExpenseStatus): string => {
    switch (status) {
      case ExpenseStatus.REIMBURSED:
      case ExpenseStatus.FINANCE_APPROVED:
        return 'green';
      case ExpenseStatus.MANAGER_APPROVED:
        return 'blue';
      case ExpenseStatus.SUBMITTED:
        return 'orange';
      case ExpenseStatus.MANAGER_REJECTED:
      case ExpenseStatus.FINANCE_REJECTED:
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="super-admin-dashboard">
        <div className="loading">
          <InlineLoader message={error === 'Waiting for authentication...' ? 'Initializing authentication' : 'Loading super admin dashboard'} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="super-admin-dashboard">
        <div className="error">
          <h3>❌ Error Loading Dashboard</h3>
          <p>{error}</p>
          
          {error.includes('Cognito') && (
            <div className="error-help" style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '12px',
              margin: '16px 0',
              fontSize: '14px'
            }}>
              <h4>💡 Troubleshooting Tips:</h4>
              <ul style={{ textAlign: 'left', margin: '8px 0' }}>
                <li>Verify your <code>REACT_APP_ADMIN_API_ENDPOINT</code> is correctly set in .env.local</li>
                <li>Ensure your Cognito Admin API Lambda function is deployed and accessible</li>
                <li>Check that your user has the necessary permissions to call the Admin API</li>
              </ul>
            </div>
          )}
          
          <button onClick={loadDashboardData} className="retry-button">🔄 Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      <div className="dashboard-header">
        <h1>🛡️ Super Admin Dashboard</h1>
        <p>System administration for {user?.name}</p>
      </div>



      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          All Expenses
        </button>
        <button 
          className={`tab-button ${activeTab === 'approvals' ? 'active' : ''}`}
          onClick={() => setActiveTab('approvals')}
        >
          Pending Approvals
          {pendingApprovals.length > 0 && (
            <span className="tab-badge">{pendingApprovals.length}</span>
          )}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="overview-tab">
          <div className="summary-cards">
            <div className="summary-card users">
              <h3>👥 Total Users</h3>
              <div className="count">{metrics.totalUsers}</div>
              <div className="description">Manage all users</div>
            </div>
            
            <div className="summary-card expenses" onClick={() => setActiveTab('expenses')}>
              <h3>📋 Total Expenses</h3>
              <div className="count">{metrics.totalExpenses}</div>
              <div className="description">All time</div>
            </div>
            
            <div className="summary-card pending" onClick={() => setActiveTab('approvals')}>
              <h3>⏳ Pending Approvals</h3>
              <div className="count">{metrics.pendingApprovals}</div>
              <div className="description">Awaiting action</div>
            </div>
            
            <div className="summary-card amount">
              <h3>💰 Total Amount</h3>
              <div className="amount">{formatAmount(metrics.totalAmount)}</div>
              <div className="description">All expenses</div>
            </div>
          </div>

          <div className="quick-actions">
            <h2>Quick Actions</h2>
            <div className="action-buttons">
              <button className="action-btn" onClick={() => navigate('/settings?tab=users')}>
                Add New User
              </button>
              <button className="action-btn" onClick={() => setActiveTab('approvals')}>
                ✅ Review Approvals
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="expenses-tab">
          <div className="expenses-header">
            <h2>All Expenses ({filteredExpenses.length})</h2>
            <button 
              className="toggle-filters-button"
              onClick={() => setShowExpenseFilters(!showExpenseFilters)}
            >
              {showExpenseFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
          
          {/* Expense Filters */}
          {showExpenseFilters && (
            <div className="expense-filters">
              {/* Quick Date Filters */}
              <div className="filter-row quick-filters">
                <label>Date:</label>
                <div className="quick-filter-buttons">
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'all' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('all')}
                  >
                    All Time
                  </button>
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'last-week' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('last-week')}
                  >
                    Last Week
                  </button>
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'last-month' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('last-month')}
                  >
                    Last Month
                  </button>
                  <button 
                    className={`quick-filter-btn ${expenseFilters.datePreset === 'custom' ? 'active' : ''}`}
                    onClick={() => handleExpenseDatePresetChange('custom')}
                  >
                    Custom
                  </button>
                </div>
              </div>
              
              <div className="filter-row">
                {/* Manager Filter */}
                <div className="filter-group">
                  <label>Manager:</label>
                  <select 
                    value={expenseFilters.manager}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, manager: e.target.value, employee: '' }))}
                  >
                    <option value="">All Managers</option>
                    {getManagers().map(manager => (
                      <option key={manager.email} value={manager.email}>
                        {manager.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Employee Filter */}
                <div className="filter-group">
                  <label>Employee:</label>
                  <select 
                    value={expenseFilters.employee}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, employee: e.target.value }))}
                  >
                    <option value="">All Employees</option>
                    {getEmployees().map(emp => (
                      <option key={emp.email} value={emp.email}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Status Filter */}
                <div className="filter-group">
                  <label>Status:</label>
                  <select 
                    value={expenseFilters.status}
                    onChange={(e) => setExpenseFilters(prev => ({ ...prev, status: e.target.value as ExpenseStatus | '' }))}
                  >
                    <option value="">All Statuses</option>
                    {Object.values(ExpenseStatus).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                
                {/* Custom Date Range */}
                {expenseFilters.datePreset === 'custom' && (
                  <>
                    <div className="filter-group">
                      <label>From:</label>
                      <input 
                        type="date"
                        value={expenseFilters.dateFrom}
                        onChange={(e) => setExpenseFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      />
                    </div>
                    <div className="filter-group">
                      <label>To:</label>
                      <input 
                        type="date"
                        value={expenseFilters.dateTo}
                        onChange={(e) => setExpenseFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                
                <button className="clear-filters-btn" onClick={clearExpenseFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          )}
          
          <div className="expenses-summary">
            <span>Showing: {filteredExpenses.length} expenses</span>
            <span>Amount: {formatAmount(filteredExpenses.reduce((sum, e) => sum + e.amount, 0))}</span>
          </div>
          
          {filteredExpenses.length === 0 ? (
            <div className="no-approvals">
              <p>No expenses found</p>
            </div>
          ) : (
            <div className="approvals-list">
              {filteredExpenses.map(expense => {
                const submitter = getUserInfo(expense.submitterId);
                return (
                  <div key={expense.id} className="approval-item">
                    <div className="approval-info">
                      <h4>{expense.title}</h4>
                      <div className="approval-meta">
                        <span className="expense-submitter">👤 {submitter.name}</span>
                        <span className="expense-amount">{formatAmount(expense.amount)}</span>
                        <span className="expense-category">{expense.category}</span>
                        <span className="expense-date">{new Date(expense.date).toLocaleDateString()}</span>
                        <span className={`expense-status ${getStatusColor(expense.status)}`}>
                          {expense.status}
                        </span>
                      </div>
                      {expense.description && (
                        <p className="expense-description">{expense.description}</p>
                      )}
                    </div>
                    
                    <div className="approval-actions">
                      <button 
                        className="approval-button view"
                        onClick={() => setSelectedExpense(expense)}
                      >
                        View
                      </button>
                      <button 
                        className="approval-button reject"
                        onClick={() => handleDeleteExpense(expense)}
                        title="Permanently delete this expense"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'approvals' && (
        <div className="approvals-tab">
          <div className="approvals-header">
            <h2>Pending Approvals</h2>
            <div className="approvals-summary">
              <span>{pendingApprovals.length} expenses awaiting approval</span>
            </div>
          </div>
          
          {pendingApprovals.length === 0 ? (
            <div className="no-approvals">
              <p>No expenses pending approval</p>
            </div>
          ) : (
            <div className="approvals-list">
              {pendingApprovals.map(expense => (
                <div key={expense.id} className="approval-item">
                  <div className="approval-info">
                    <h4>{expense.title}</h4>
                    <div className="approval-meta">
                      <span className="expense-amount">{formatAmount(expense.amount)}</span>
                      <span className="expense-category">{expense.category}</span>
                      <span className="expense-date">{new Date(expense.date).toLocaleDateString()}</span>
                      <span className={`expense-status ${getStatusColor(expense.status)}`}>
                        {expense.status}
                      </span>
                    </div>
                    {expense.description && (
                      <p className="expense-description">{expense.description}</p>
                    )}
                  </div>
                  
                  <div className="approval-actions">
                    <button 
                      className="approval-button view"
                      onClick={() => setSelectedExpense(expense)}
                    >
                      View Details
                    </button>
                    
                    {expense.status === ExpenseStatus.MANAGER_APPROVED && (
                      <>
                        <button 
                          className="approval-button approve"
                          onClick={() => {
                            const comments = prompt('Enter approval comments (optional):');
                            if (comments !== null) {
                              handleFinalApproval(expense, comments || undefined);
                            }
                          }}
                        >
                          Final Approve
                        </button>
                        <button 
                          className="approval-button reject"
                          onClick={() => {
                            const comments = prompt('Enter rejection reason:');
                            if (comments) {
                              handleRejectExpense(expense, comments);
                            }
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    
                    {expense.status === ExpenseStatus.FINANCE_APPROVED && (
                      <button 
                        className="approval-button reimburse"
                        onClick={() => handleMarkReimbursed(expense)}
                      >
                        Mark Reimbursed
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div className="expense-detail-modal">
          <div className="modal-backdrop" onClick={() => setSelectedExpense(null)} />
          <div className="modal-content">
            <ExpenseDetail 
              expense={selectedExpense}
              onClose={() => setSelectedExpense(null)}
              onUpdate={loadDashboardData}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;

